/**
 * CompetencyGrowthSidebar — calm contextual coaching surface for
 * Work Mode (Stage-2 P3, Feb 18, 2026).
 *
 * Operator brief (paraphrased + locked):
 *   "Quiet contextual growth support — helpful coaching awareness,
 *    NOT surveillance or assessment pressure. Collapsible, low
 *    visual noise, no popups, no auto-scoring, no auto-advance."
 *
 * Doctrine:
 *   • Read-only by default. No actions that grade or score.
 *   • Calm slate palette, no red, no panic glyphs.
 *   • Drive Mode aware — moving / unknown collapses to glance-only.
 *   • Evidence logging is manual + parked-only (server enforces).
 *   • Future hooks (AI evidence, Goal Outcome Loop, Progressive
 *     Environmental Exposure, PDI standards-check) are referenced
 *     in copy/data but NOT wired here.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Sprout, ChevronDown, ChevronUp, Plus, Check,
} from "lucide-react";
import useCompetencyGrowth from "../../hooks/useCompetencyGrowth";
import useDriveModeState from "../../hooks/useDriveModeState";
import { driveModeCapabilities } from "../../lib/driveMode";


const FOCUS_OPTIONS = [
  { key: "",                     label: "— focus —" },
  { key: "roundabouts",          label: "Roundabouts" },
  { key: "junctions",            label: "Junctions" },
  { key: "parking",              label: "Parking" },
  { key: "meeting_traffic",      label: "Meeting traffic" },
  { key: "dual_carriageways",    label: "Dual carriageways" },
  { key: "hill_starts",          label: "Hill starts" },
  { key: "crossroads",           label: "Crossroads" },
  { key: "pedestrian_crossings", label: "Pedestrian crossings" },
];

const ENV_OPTIONS = [
  { key: "",                  label: "— environment —" },
  { key: "residential",       label: "Residential" },
  { key: "urban",             label: "Urban" },
  { key: "suburban",          label: "Suburban" },
  { key: "dual_carriageway",  label: "Dual carriageway" },
  { key: "rural",             label: "Rural" },
  { key: "car_park",          label: "Car park" },
  { key: "town_centre",       label: "Town centre" },
  { key: "mixed",             label: "Mixed setting" },
];

const COMPLEXITY_OPTIONS = [
  { key: "",  label: "— complexity —" },
  { key: 1,   label: "Level 1 — small / simple" },
  { key: 2,   label: "Level 2 — medium" },
  { key: 3,   label: "Level 3 — busy / larger" },
  { key: 4,   label: "Level 4 — complex / advanced" },
];

const STAGE_OPTIONS = [
  { key: "",                label: "— stage —" },
  { key: "instructor_led",  label: "Instructor-led" },
  { key: "shared",          label: "Shared" },
  { key: "learner_owned",   label: "Learner-owned" },
];


// Local storage key for sticky context selection within a session.
const CTX_KEY = "wm_competency_growth_ctx_v1";

function loadContext() {
  try {
    const raw = localStorage.getItem(CTX_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveContext(ctx) {
  try { localStorage.setItem(CTX_KEY, JSON.stringify(ctx)); } catch {}
}


export default function CompetencyGrowthSidebar({
  lessonId,
  learnerId,
  surface = "dark",
}) {
  const [open, setOpen] = useState(false);
  const [ctx, setCtx] = useState(() => loadContext());
  const [feedback, setFeedback] = useState(null);

  const { movingState: localMovingState } = useDriveModeState();
  const movingState = localMovingState || "unknown";
  const driveMode = driveModeCapabilities(movingState);
  const interactive = Boolean(driveMode?.capabilities?.interactive_actions);

  const {
    prompts, summary, loading, logEvidence,
  } = useCompetencyGrowth({
    focus:       ctx.focus,
    environment: ctx.environment,
    complexity:  ctx.complexity ? Number(ctx.complexity) : undefined,
    stage:       ctx.stage,
    movingState,
    enabled:     Boolean(lessonId),
  });

  // Persist context on change.
  useEffect(() => { saveContext(ctx); }, [ctx]);

  // While moving / unknown — the body cannot expand. Quiet doctrine.
  const expandable = interactive;
  const isOpen = expandable && open;

  const isDark = surface === "dark";
  const wrapCls = isDark
    ? "bg-slate-800/70 border-slate-700 text-slate-200"
    : "bg-slate-50 border-slate-200 text-slate-700";
  const dividerCls = isDark ? "border-slate-700/60" : "border-slate-200/60";
  const labelMutedCls = isDark ? "text-slate-400/80" : "text-slate-500";
  const subTextCls = isDark ? "text-slate-300" : "text-slate-600";

  const headerLine = summary?.summary || "Quiet coaching awareness.";

  // Top "leading" prompt for the glance header — same prompts list
  // we'd show in the body, just first one.
  const leadingPrompt = prompts?.prompts?.[0]?.text || null;

  const handleLogEvidence = async (competencyKey) => {
    if (!lessonId || !competencyKey) return;
    setFeedback(null);
    const res = await logEvidence({
      lessonId,
      learnerId,
      competency: competencyKey,
    });
    if (res.ok) {
      setFeedback({ tone: "ok", text: "Logged. Quietly noted." });
    } else if (res.code === "drive_mode_glance_only") {
      setFeedback({
        tone: "muted",
        text: "Glance-only while moving. Log when parked.",
      });
    } else {
      setFeedback({ tone: "muted", text: res.message || "Could not log." });
    }
    setTimeout(() => setFeedback(null), 3500);
  };

  const ctxCount = useMemo(() => {
    return ["focus", "environment", "complexity", "stage"]
      .filter((k) => ctx[k]).length;
  }, [ctx]);

  if (!lessonId) return null;

  return (
    <div
      data-testid="competency-growth-sidebar"
      data-moving-state={movingState}
      data-interactive={interactive ? "true" : "false"}
      data-context-count={String(ctxCount)}
      className={`rounded-xl border ${wrapCls} overflow-hidden`}
    >
      {expandable ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="competency-growth-toggle"
          aria-expanded={isOpen}
          className="w-full flex items-center gap-3 px-4 py-3 text-left"
        >
          <Sprout className="w-4 h-4 flex-none opacity-70" />
          <div className="flex-1 min-w-0">
            <div
              className="text-[11px] font-semibold uppercase tracking-wide opacity-70"
              data-testid="competency-growth-heading"
            >
              Competency Growth
            </div>
            <div
              className="text-sm font-semibold truncate"
              data-testid="competency-growth-summary"
            >
              {headerLine}
            </div>
          </div>
          {isOpen ? (
            <ChevronUp className="w-4 h-4 flex-none opacity-50" />
          ) : (
            <ChevronDown className="w-4 h-4 flex-none opacity-50" />
          )}
        </button>
      ) : (
        <div
          data-testid="competency-growth-glance"
          className="w-full flex items-center gap-3 px-4 py-3"
        >
          <Sprout className="w-4 h-4 flex-none opacity-70" />
          <div className="flex-1 min-w-0">
            <div
              className="text-[11px] font-semibold uppercase tracking-wide opacity-70"
              data-testid="competency-growth-heading"
            >
              Competency Growth
            </div>
            <div
              className="text-sm font-semibold truncate"
              data-testid="competency-growth-summary"
            >
              {leadingPrompt || headerLine}
            </div>
          </div>
        </div>
      )}

      {isOpen ? (
        <div
          data-testid="competency-growth-body"
          className={`px-4 pb-4 pt-1 text-sm border-t ${dividerCls} ${subTextCls}`}
        >
          {/* Quiet context picker — single row of selects. */}
          <div
            className="grid grid-cols-2 gap-2 mb-3"
            data-testid="competency-growth-context"
          >
            <ContextSelect
              testid="competency-growth-focus"
              label="Focus"
              value={ctx.focus || ""}
              onChange={(v) => setCtx((c) => ({ ...c, focus: v || undefined }))}
              options={FOCUS_OPTIONS}
              isDark={isDark}
            />
            <ContextSelect
              testid="competency-growth-environment"
              label="Environment"
              value={ctx.environment || ""}
              onChange={(v) => setCtx((c) => ({ ...c, environment: v || undefined }))}
              options={ENV_OPTIONS}
              isDark={isDark}
            />
            <ContextSelect
              testid="competency-growth-complexity"
              label="Complexity"
              value={ctx.complexity || ""}
              onChange={(v) => setCtx((c) => ({ ...c, complexity: v ? Number(v) : undefined }))}
              options={COMPLEXITY_OPTIONS}
              isDark={isDark}
            />
            <ContextSelect
              testid="competency-growth-stage"
              label="Stage"
              value={ctx.stage || ""}
              onChange={(v) => setCtx((c) => ({ ...c, stage: v || undefined }))}
              options={STAGE_OPTIONS}
              isDark={isDark}
            />
          </div>

          {/* Prompt list — calm bullet style. */}
          <div
            className={`rounded-lg border ${dividerCls} p-3 mb-3`}
            data-testid="competency-growth-prompts"
          >
            {loading ? (
              <p className={`text-[12px] ${labelMutedCls}`}>Looking…</p>
            ) : prompts?.prompts?.length ? (
              <ul className="space-y-2">
                {prompts.prompts.map((p, idx) => (
                  <li
                    key={`${p.competency}-${idx}`}
                    data-testid={`competency-growth-prompt-${idx}`}
                    data-competency={p.competency}
                    className="flex items-start gap-2"
                  >
                    <span className={`mt-1.5 inline-block w-1.5 h-1.5 rounded-full flex-none ${
                      isDark ? "bg-slate-400" : "bg-slate-500"
                    }`} />
                    <div className="min-w-0">
                      <div
                        className={`text-[11px] uppercase tracking-wide ${labelMutedCls}`}
                        data-testid={`competency-growth-prompt-tag-${idx}`}
                      >
                        {p.competency_label}
                      </div>
                      <div className="text-[13px] leading-snug">
                        {p.text}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleLogEvidence(p.competency)}
                        data-testid={`competency-growth-log-${idx}`}
                        data-competency={p.competency}
                        className={`mt-1 inline-flex items-center gap-1.5 text-[11px] font-medium underline-offset-2 hover:underline ${
                          isDark ? "text-slate-200" : "text-slate-700"
                        }`}
                      >
                        <Plus className="w-3 h-3 opacity-70" />
                        Quietly note evidence
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p
                className={`text-[12px] ${labelMutedCls}`}
                data-testid="competency-growth-empty"
              >
                {prompts?.prompt ||
                  "Choose a focus, environment, or stage to surface calm coaching opportunities."}
              </p>
            )}
          </div>

          {/* Quiet daily growth line. */}
          <div
            data-testid="competency-growth-daily"
            className={`text-[12px] ${labelMutedCls}`}
          >
            {summary?.summary}
          </div>

          {feedback ? (
            <div
              data-testid="competency-growth-feedback"
              data-tone={feedback.tone}
              className={`mt-2 inline-flex items-center gap-1.5 text-[11px] ${
                feedback.tone === "ok"
                  ? (isDark ? "text-slate-100" : "text-slate-800")
                  : labelMutedCls
              }`}
            >
              {feedback.tone === "ok" ? (
                <Check className="w-3 h-3 opacity-70" />
              ) : null}
              <span>{feedback.text}</span>
            </div>
          ) : null}

          <p className={`mt-3 text-[11px] ${labelMutedCls}`}>
            Coaching awareness — no scoring, no auto-advance. You stay
            in control.
          </p>
        </div>
      ) : null}
    </div>
  );
}


function ContextSelect({ testid, label, value, onChange, options, isDark }) {
  const baseCls = isDark
    ? "bg-slate-900/70 border-slate-700 text-slate-200"
    : "bg-white border-slate-300 text-slate-800";
  const labelCls = isDark ? "text-slate-400" : "text-slate-500";
  return (
    <label className="flex flex-col gap-1 min-w-0">
      <span className={`text-[10px] uppercase tracking-wide ${labelCls}`}>
        {label}
      </span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`text-[12px] rounded-md border px-2 py-1.5 ${baseCls} focus:outline-none focus:ring-1`}
      >
        {options.map((o) => (
          <option key={String(o.key)} value={o.key}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
